import { Box, Button, Checkbox, Chip, CircularProgress, Typography } from '@mui/material';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DrawerHeader } from '../../../../../components/Drawer/DrawerHeader';
import { useEffect, useState } from 'react';
import {
    useLazyGetReconfirmationSelectionEmployeesQuery,
    useCreateReconfirmationBatchMutation
} from '../../../../../services/periodicosApi';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch } from '../../../../../hooks/reduxHooks';
import { showAlert } from '../../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../../constants/values';
import { handleErrors } from '../../../../../utils/common';
import BackdropComponent from '../../../../../components/Backdrop/BackdropComponent';

interface Props {
    handleCloseDrawer: () => void;
    selectedBatchId?: number | null;
}

const PeriodicosCreateReconfirmationBatchDrawer = ({ handleCloseDrawer, selectedBatchId }: Props) => {
    const [fetchEmployees, { data, isFetching, isError }] = useLazyGetReconfirmationSelectionEmployeesQuery();
    const [createReconfirmationBatch, { isLoading }] = useCreateReconfirmationBatchMutation();
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (selectedBatchId) {
            fetchEmployees(selectedBatchId);
        }
    }, [selectedBatchId]);


    const employees: any[] = Array.isArray(data?.data?.empleados) ? data.data?.empleados : [];

    useEffect(() => {
        if (employees.length > 0) {
            setSelectedIds(new Set(employees.map((e: any) => e.idEmpleadoLote).filter(Boolean)));
        }
    }, [data]);

    const allSelected = employees.length > 0 && employees.every((e: any) => selectedIds.has(e.idEmpleadoLote));
    const indeterminate = selectedIds.size > 0 && !allSelected;

    const handleToggle = (id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleToggleAll = () => {
        if (allSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(employees.map((e: any) => e.idEmpleadoLote).filter(Boolean)));
        }
    };

    const handleConfirm = async () => {
        if (!selectedBatchId || selectedIds.size === 0) return;
        try {
            const res: any = await createReconfirmationBatch({
                batchId: selectedBatchId,
                idsEmpleadosLote: Array.from(selectedIds)
            });
            if (res?.error) {
                handleErrors(res.error);
                return;
            }
            enqueueSnackbar('Lote de reconfirmación generado correctamente.', { variant: 'success' });
            handleCloseDrawer();
        } catch {
            dispatch(
                showAlert({
                    title: 'No se pudo generar el lote de reconfirmación',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const renderContent = () => {
        if (isFetching) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={32} />
                </Box>
            );
        }

        if (isError) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1 }}>
                    <ErrorOutlineIcon sx={{ color: '#ef4444', fontSize: 32 }} />
                    <Typography sx={{ fontSize: '0.875rem', color: '#ef4444', textAlign: 'center' }}>
                        Ocurrió un error al obtener los empleados.
                    </Typography>
                </Box>
            );
        }

        if (employees.length === 0) {
            return (
                <Typography sx={{ fontSize: '0.875rem', color: '#94a3b8', textAlign: 'center', py: 6 }}>
                    No se encontraron empleados habilitados para reconfirmación.
                </Typography>
            );
        }

        return (
            <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 0.5 }}>
                    <Checkbox
                        checked={allSelected}
                        indeterminate={indeterminate}
                        onChange={handleToggleAll}
                        size="small"
                    />
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#334155' }}>
                        {`Seleccionar todos (${employees.length})`}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {employees.map((emp: any) => {
                        const isSelected = selectedIds.has(emp.idEmpleadoLote);
                        return (
                            <Box
                                key={emp.idEmpleadoLote}
                                onClick={() => emp.idEmpleadoLote && handleToggle(emp.idEmpleadoLote)}
                                sx={{
                                    border: '1.5px solid',
                                    borderColor: isSelected ? 'primary.main' : '#e2e8f0',
                                    borderRadius: 2,
                                    p: 1.5,
                                    cursor: 'pointer',
                                    bgcolor: isSelected ? '#eff6ff' : 'background.paper',
                                    transition: 'border-color 0.15s, background-color 0.15s',
                                    '&:hover': { boxShadow: 1 }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <Checkbox
                                        checked={isSelected}
                                        size="small"
                                        onChange={() => emp.idEmpleadoLote && handleToggle(emp.idEmpleadoLote)}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{ mt: -0.5, flexShrink: 0 }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                                            {`${emp?.apellido || '-'}, ${emp?.nombre || '-'}`}
                                        </Typography>
                                        {emp?.cuil && (
                                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b', fontFamily: 'monospace' }}>
                                                CUIL: {emp.cuil}
                                            </Typography>
                                        )}
                                        {emp?.examenes?.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                {emp.examenes.map((exam: any) => (
                                                    <Chip
                                                        key={exam.idExamenEmpleado}
                                                        label={exam.descripcion?.trim() || `Examen ${exam.idExamenEmpleado}`}
                                                        size="small"
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            bgcolor: '#fef3c7',
                                                            color: '#92400e',
                                                            border: '1px solid #fcd34d',
                                                            fontWeight: 500,
                                                            height: 22
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </>
        );
    };

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" sx={{ height: '100%' }}>
            <Box sx={{ overflow: 'auto', pb: 2 }}>
                <DrawerHeader
                    onIconClick={handleCloseDrawer}
                    text="Generar Lote de Reconfirmación"
                />
                <Box sx={{ px: 2, pt: 1.5 }}>
                    <Typography sx={{ fontSize: '0.875rem', color: '#475569', mb: 2 }}>
                        Seleccioná los empleados habilitados para reconfirmación. Se generará un nuevo lote con los empleados seleccionados.
                    </Typography>
                    {renderContent()}
                </Box>
            </Box>

            <BackdropComponent loading={isLoading} />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    pt: 2,
                    px: 2,
                    borderTop: 1,
                    borderColor: 'divider'
                }}
            >
                <Box>
                    {selectedIds.size > 0 && (
                        <Typography sx={{ fontSize: '0.8125rem', color: '#2563eb', fontWeight: 600 }}>
                            {`${selectedIds.size} empleado${selectedIds.size !== 1 ? 's' : ''} seleccionado${selectedIds.size !== 1 ? 's' : ''}`}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={handleCloseDrawer} disabled={isLoading}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={selectedIds.size === 0 || isLoading || isFetching}
                        startIcon={<ReplayOutlinedIcon fontSize="small" />}
                        sx={{ fontWeight: 600, textTransform: 'none' }}
                    >
                        Generar Lote
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export { PeriodicosCreateReconfirmationBatchDrawer };
